import { mapState } from 'vuex';
import dateUtils from '@/common/dateUtils';
import dateTimeMixin from '@/mixins/dateTimeMixin';
import common from '@/mixins/common';
import { ROUTE_NAMES } from '@/modules/customerVbzWorksheet/routes';
import dataUtils from '@/common/dataUtils';
import worksheetService from '@/modules/customerVbzWorksheet/worksheetService';

const wsMixin = {
  mixins: [common, dateTimeMixin],
  data() {
    return {
      colorPalette: ['#40930b', '#4c9bc2', '#e861dc', '#3e5ca9'],
    };
  },
  computed: {
    ...mapState('vbzWorksheet', [
      'contextDate',
      'informationsByDate',
      'pikettsByDate',
      'reasonsById',
      'illnessReasonsById',
      'remarksById',
      'absenceDurationsById',
      'zueri9ReasonsById',
      'zueri9ClientsById',
      'zueri9States',
      'zueri9StatesById',
      'zueri9MonthlyExpectedHours',
      'zueri9MonthlyExpectedHoursById',
      'dispoStates',
      'dispoStatesById',
      'userByCompanyNr',
      'userByEmployeeNr',
    ]),
    ...mapState('profile', ['user']),
    mx_activeInfoFeedsAtSelectedDate() {
      let key = worksheetService.buildDateIdFromDate(this.contextDate);
      let infos = this.informationsByDate[key];

      let timeThreshold = Date.now() - 1000 * 10; // for testing 10s

      return infos ? infos.filter((i) => !i.deleted && i.receivedAt && i.receivedAt > 0).length : 0;
    },
    mx_activePikettsAtSelectedDate() {
      let key = worksheetService.buildDateIdFromDate(this.contextDate);
      const piketts = this.pikettsByDate[key];
      if (!piketts) return 0;

      const applicablePiketts = piketts.filter((i) => {
        const view = i.createdInView;
        const isApplicableView = this.$route.name.includes(view);
        const isReceivedAtLaterThanThreshold = i.receivedAt > 0;
        // return isApplicableView && !i.deleted && i.receivedAt && Date.parse(i.receivedAt)>timeThreshold});
        return isApplicableView && !i.deleted && isReceivedAtLaterThanThreshold;
      });
      return applicablePiketts.length;
    },
    mx_openAbsencesAtSelectedDateInDispoView() {
      let key = worksheetService.buildDateIdFromDate(this.contextDate);
      let absences = this.absencesByDate[key];
      let openState = this.mx_dispoStates.open;
      return absences ? absences.filter((i) => !i.deleted && i.assignedTo === 'dispo' && i.dispoState === openState) : [];
    },
    mx_openAbsencesAtSelectedDateInZueri9View() {
      let key = worksheetService.buildDateIdFromDate(this.contextDate);
      let absences = this.absencesByDate[key];
      let openState = this.mx_zueri9States.open;
      return absences ? absences.filter((i) => !i.deleted && i.assignedTo === 'zueri9' && i.zueri9State === openState) : [];
    },
    //TODO: Rename and use mx_dispoStatesObj
    mx_dispoStates() {
      const obj = {};
      Object.keys(this.mx_dispoStatesObj).forEach((key) => (obj[key] = this.mx_dispoStatesObj[key].id));
      return obj;
    },
    mx_dispoStatesObj() {
      let statesBySystemValue = dataUtils.buildIdMap(this.dispoStates, 'systemValue');
      let systemValues = {
        open: statesBySystemValue['open'],
        inProgress: statesBySystemValue['in progress'],
        completed: statesBySystemValue['completed'],
        forwarded: statesBySystemValue['forwarded'],
        returnedAndOpen: statesBySystemValue['returnedAndOpen'],
      };
      return systemValues;
    },
    mx_dispoOpenStateColor() {
      return this.mx_dispoStatesObj.open && this.mx_dispoStatesObj.open.bgColor;
    },
    //TODO: Rename and use mx_zueri9StatesObj
    mx_zueri9States() {
      const obj = {};
      Object.keys(this.mx_zueri9StatesObj).forEach((key) => (obj[key] = this.mx_zueri9StatesObj[key].id));

      return obj;
    },
    /**
     * @typedef {Object} StateInfo
     * @property {number} id - Unique identifier for the state.
     * @property {string} text - Descriptive text for the state.
     * @property {string} bgColor - Background color associated with the state, in HEX format.
     * @property {boolean} system - Indicates if the state is system-defined.
     * @property {string} systemValue - System value that categorizes the state.
     * @property {string} createdAt - ISO 8601 formatted string representing the date and time the state was created.
     * @property {string} updatedAt - ISO 8601 formatted string representing the date and time the state was last updated.
     */
    /**
     *
     * @return {Object.<string, StateInfo>}
     */
    mx_zueri9StatesObj() {
      let statesBySystemValue = dataUtils.buildIdMap(this.zueri9States, 'systemValue');
      let systemValues = {
        open: statesBySystemValue['open'],
        inProgress: statesBySystemValue['in progress'],
        completed: statesBySystemValue['completed'],
        returnedToAuthor: statesBySystemValue['returnedToAuthor'],
      };
      return systemValues;
    },
    mx_zueri9OpenStateColor() {
      return this.mx_zueri9StatesObj.open && this.mx_zueri9StatesObj.open.bgColor;
    },
    mx_isDispoView() {
      return [ROUTE_NAMES.dispo, ROUTE_NAMES.dispoReadOnly].includes(this.$route.name);
    },
    mx_isZueri9View() {
      return this.$route.name === ROUTE_NAMES.zueri9;
    },
    mx_isZueri9CustomerView() {
      return this.$route.name === ROUTE_NAMES.zueri9CustomerView;
    },
    mx_userGroups() {
      return this.user.groups ? this.user.groups.split(',') : [];
    },
  },

  methods: {
    mx_isUserPermittedWhenRestrictedToGroups(restrictedToGroups) {
      return worksheetService.isUserPermittedWhenRestrictedToGroups(this.mx_userGroups, restrictedToGroups);
      // let groupInRequiredGroups = restrictedToGroups.find((group) => this.mx_userGroups.indexOf(group) > -1);
      // return !!groupInRequiredGroups;
    },
    mx_notCompletedAbsencesAtGivenDate(date) {
      let key = worksheetService.buildDateIdFromDate(date);
      let absences = this.absencesByDate[key] || [];
      let doneStates = [this.mx_dispoStates.completed, this.mx_dispoStates.forwarded];

      return absences.filter((a) => {
        return a.deleted === false && !doneStates.includes(a.dispoState);
      });
    },
    mx_absenceDuration(absence) {
      if (!absence.absenceDurationId) return '';

      let absenceDuration = this.absenceDurationsById[absence.absenceDurationId];
      return absenceDuration ? absenceDuration.text : ``;
    },

    mx_absenceDurationSorter(a, b) {
      let aResolved = this.mx_absenceDuration(a);
      let bResolved = this.mx_absenceDuration(b);

      return aResolved.localeCompare(bResolved);
    },

    mx_reason(absence) {
      let reason = this.reasonsById[absence.reasonId];
      return reason ? reason.text : ``;
    },

    mx_reasonSorter(a, b) {
      let aResolved = this.mx_reason(a);
      let bResolved = this.mx_reason(b);

      return aResolved.localeCompare(bResolved);
    },

    mx_backup1Until(absence) {
      return dateUtils.formatTime(absence.backup1_until);
    },

    mx_backup1UntilSorter(a, b) {
      let aResolved = this.mx_backup1Until(a);
      let bResolved = this.mx_backup1Until(b);

      return aResolved.localeCompare(bResolved);
    },
    mx_from(absence, fieldName = 'dutyFrom') {
      return absence[fieldName] ? dateUtils.formatTime(absence[fieldName]) : '';
    },

    mx_fromLocation(absence, fieldName = 'dutyFromLoc') {
      return `${absence[fieldName] ? absence[fieldName] : ''}`;
    },
    mx_fromTimeAndLocation(absence) {
      return `${this.mx_from(absence)} ${this.mx_fromLocation(absence)}`;
    },
    mx_z9FromTimeAndLocation(absence) {
      if (absence.z9DutyFrom) {
        return `${this.mx_from(absence, 'z9DutyFrom')} ${this.mx_fromLocation(absence, 'z9DutyFromLoc')}`;
      }
      return '';
    },
    mx_to(absence, fieldName = 'dutyUntil') {
      return absence[fieldName] && dateUtils.formatTime(absence[fieldName]) ? dateUtils.formatTime(absence[fieldName]) : '';
    },
    mx_toLocation(absence, fieldName = 'dutyUntilLoc') {
      return `${absence[fieldName] ? absence[fieldName] : ''}`;
    },
    mx_toTimeAndLocation(absence) {
      return `${this.mx_to(absence)} ${this.mx_toLocation(absence)}`;
    },
    mx_z9ToTimeAndLocation(absence) {
      return `${this.mx_to(absence, 'z9DutyUntil')} ${this.mx_toLocation(absence, 'z9DutyUntilLoc')}`;
    },
    mx_fromTo(absence) {
      return this.mx_fromTimeAndLocation(absence) + ' - ' + this.mx_toTimeAndLocation(absence);
    },
    mx_z9FromTo(absence) {
      return this.mx_fromToZ9(absence);
    },
    mx_fromToZ9(absence) {
      return this.mx_z9FromTimeAndLocation(absence) + ' - ' + this.mx_z9ToTimeAndLocation(absence);
    },
    mx_timeDifferenceDecimal(absence, fromField = 'dutyFrom', untilField = 'dutyUntil') {
      const from = absence[fromField];
      const to = absence[untilField];
      if (absence.id === 69223) {
        console.debug(`69223 ${fromField}(${from} - ${untilField} (${to}) = ${worksheetService.calculateTimeDifferenceDecimal(from, to)},`);
      }
      return worksheetService.calculateTimeDifferenceDecimal(from, to);
    },

    mx_fromToSorter(a, b) {
      let aResolved = this.mx_fromTo(a);
      let bResolved = this.mx_fromTo(b);

      return aResolved.localeCompare(bResolved);
    },
    mx_fromToZ9Sorter(a, b) {
      let aResolved = this.mx_fromToZ9(a);
      let bResolved = this.mx_fromToZ9(b);
      console.debug(`sort aResolved=${aResolved} > bResolved=${bResolved}`);
      return aResolved.localeCompare(bResolved);
    },
    mx_reportedAtTime(absence) {
      // console.debug('absence updatedAt', absence.updatedAt);
      if (this.mx_isZueri9View && absence.updatedAt) {
        return this.mx_formatIsoDateStringAsLocaleDateTime(absence.updatedAt);
      }
      return absence.reportedAtTime ? this.mx_formatIsoDateStringAsLocaleDateTime(absence.reportedAtTime) : '';
    },
    mx_lastModified(absence) {
      return `${this.mx_formatIsoDateStringAsLocaleDateTime(absence.updatedAt)}`;
    },
    mx_createdAt(absence) {
      return this.mx_formatIsoDateStringAsLocaleDateTime(absence.createdAt);
    },
    mx_updatedAt(absence) {
      return this.mx_formatIsoDateStringAsLocaleDateTime(absence.updatedAt);
    },
    mx_reportedAtTimeSorter(a, b) {
      let aResolved = new Date(Date.parse(a.reportedAtTime));
      let bResolved = new Date(Date.parse(b.reportedAtTime));
      if (!aResolved.getMonth) return -1;
      if (!bResolved.getMonth) return 1;
      return aResolved.getTime() - bResolved.getTime() < 0 ? -1 : 1;
    },
    mx_resolveUserByCompanyNr(companyNr) {
      return this.userByCompanyNr[companyNr];
    },
    mx_resolveEmployeeNr(employeeNr) {
      return this.userByEmployeeNr[employeeNr];
    },
    /**
     * @desc Gives the full name of the user back with the Number attached at the end in parentheses.
     * @param companyNr
     * @return {string|*}
     */
    mx_formatUserFromCompanyNr(companyNr) {
      if (!companyNr) return '';
      let user = this.mx_resolveUserByCompanyNr(companyNr);
      return user ? this.mx_formatUser(user) : companyNr;
    },
    /**
     * @desc Gives the full name of the user back without the Number attached at the end.
     * @param companyNr
     * @return {string|string}
     */
    mx_formatUserFromCompanyNrWithNoCompanyNr(companyNr) {
      let user = this.mx_resolveUserByCompanyNr(companyNr);
      return user ? this.mx_formatUserNoNumber(user) : 'kein Name';
    },
    /**
     * @desc Gives the full name back when provided with the user.
     * @param user
     * @return {string}
     */
    mx_formatUserNoNumber(user) {
      if (user) return `${user.lastname} ${user.firstname}`;
      return '';
    },
    mx_formatUser(user) {
      // to debug
      if (user && !user.lastname && !user.firstname && !user.companyNr) return null;
      if (user) return `${user.lastname || ''} ${user.firstname || ''} (${user.companynr ? user.companynr : 'keine VbzNr.'})`;
      return '';
    },
    mx_formatUserSorter(a, b) {
      let aResolved = this.mx_formatUser(a);
      let bResolved = this.mx_formatUser(b);

      return aResolved.localeCompare(bResolved);
    },
    mx_resolveEmployeeNrToCompanyNr(employeenr) {
      const user = this.userByEmployeeNr[employeenr];
      return user ? user.companynr : null;
    },
    mx_resolveUserEmployeeNr(employeenr) {
      return this.userByEmployeeNr[employeenr];
    },
    mx_zueri9VbzNr(absence) {
      let vbzNr = absence.zueri9VbzNr;
      let user = this.userByCompanyNr[Number(vbzNr)];
      if (user) return `${user.lastname} ${user.firstname} (${vbzNr})`;

      return vbzNr ? `${vbzNr}` : '';
    },
    mx_zueri9VbzNrSortValue(absence) {
      let vbzNr = absence.zueri9VbzNr;
      let user = this.userByCompanyNr[Number(vbzNr)];
      if (user) return `${user.lastname} ${user.firstname} (${vbzNr})`;

      return vbzNr ? `${vbzNr}` : '';
    },
    mx_zueri9VbzNrSorter(a, b) {
      let aResolved = this.mx_zueri9VbzNrSortValue(a);
      let bResolved = this.mx_zueri9VbzNrSortValue(b);

      return aResolved.localeCompare(bResolved);
    },
    mx_fromToTwoLines(absence) {
      return `${absence.line} ${this.mx_fromTo(absence)}`;
    },
    mx_fromToTwoLinesSorter(a, b) {
      let aResolved = this.mx_fromToTwoLines(a);
      let bResolved = this.mx_fromToTwoLines(b);

      return aResolved.localeCompare(bResolved);
    },
    mx_fromToZ9TwoLines(absence) {
      return `${this.mx_z9FromTo(absence)}`;
    },
    mx_fromToZ9TwoLinesSorter(a, b) {
      let aResolved = this.mx_fromToZ9TwoLines(a);
      let bResolved = this.mx_fromToZ9TwoLines(b);

      return aResolved.localeCompare(bResolved);
    },
    mx_zueri9Client(absence) {
      let zueri9Client = this.zueri9ClientsById[absence.zueri9ClientId];
      return zueri9Client ? zueri9Client.text : ``;
    },
    mx_zueri9Reason(absence) {
      let zueri9Reason = this.zueri9ReasonsById[absence.zueri9ReasonId];
      return zueri9Reason ? zueri9Reason.text : ``;
    },
    mx_zueri9ReasonSorter(a, b) {
      let aResolved = this.mx_zueri9Reason(a);
      let bResolved = this.mx_zueri9Reason(b);

      return aResolved.localeCompare(bResolved);
    },
    mx_zueri9ClientSorter(a, b) {
      let aResolved = this.mx_zueri9Client(a);
      let bResolved = this.mx_zueri9Client(b);

      return aResolved.localeCompare(bResolved);
    },
    mx_zueri9ClientAndReason(absence) {
      return `${this.mx_zueri9Client(absence)} ${this.mx_zueri9Reason(absence)}`;
    },
    mx_zueri9ClientAndReasonSorter(a, b) {
      let aResolved = this.mx_zueri9ClientAndReason(a);
      let bResolved = this.mx_zueri9ClientAndReason(b);

      return aResolved.localeCompare(bResolved);
    },

    mx_remark(absence) {
      let remark = this.remarksById[absence.remarkId];
      return remark ? remark.text : ``;
    },
    mx_zueri9State(absence) {
      let state = this.zueri9StatesById[absence.zueri9State];
      return state ? state.text : '';
    },
    mx_zueri9StateBgColor(absence) {
      let state = this.zueri9StatesById[absence.zueri9State];
      return state ? state.bgColor : '';
    },
    mx_dispoState(absence) {
      let state = this.dispoStatesById[absence.dispoState];
      return state ? state.text : '';
    },
    mx_dispoStateBgColor(absence) {
      let state = this.dispoStatesById[absence.dispoState];
      return state ? state.bgColor : '';
    },
    mx_zueri9StateSorter(a, b) {
      let aResolved = this.mx_zueri9State(a);
      let bResolved = this.mx_zueri9State(b);

      return aResolved.localeCompare(bResolved);
    },
    mx_dispoStateSorter(a, b) {
      let aResolved = this.mx_dispoState(a);
      let bResolved = this.mx_dispoState(b);

      return aResolved.localeCompare(bResolved);
    },
    mx_dutyNameSorter(a, b) {
      let aResolved = a.dutyname || '';
      let bResolved = b.dutyname || '';

      return aResolved.localeCompare(bResolved);
    },
  },
};
export default wsMixin;
